import { Component, computed, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { ILpActivity, NineSlicedContainerComponent, Rect, Vector2 } from '@gametailors/leerplus';
import { delay } from '@gametailors/web-core/misc';
import { LpRidScrapbookActivity, LpRidScrapbookReward } from '../../../../typings/schema';
import { RidButton } from '../../../components/rid-button/rid-button';
import { RidDraggable } from '../../../components/rid-draggable/rid-draggable';
import { RidAssignmentBackground } from '../../components/rid-assignment-background/rid-assignment-background';
import { RidDrawingTool, RidDrawingTray } from '../../components/rid-drawing-tray/rid-drawing-tray';
import { RidScrapbookDrawingInput } from '../../components/rid-scrapbook-drawing-input/rid-scrapbook-drawing-input';
import { RidScrapbookDrawing } from '../../components/rid-scrapbook-drawing/rid-scrapbook-drawing';
import { LEERPLUS } from '../../rid-game.utils';
import { RidPlacedReward, RidRewardService } from '../../services/rid-reward.service';
import { RidScrapbookService } from '../../services/rid-scrapbook.service';

const REWARD_HEIGHT = 96;
const FLOATING_REST_LEFT = '-236px';
const FLOATING_REST_TOP = '50%';
const REST_ROTATION = 0;
const PLACED_ROTATION = 3;
const IDLE_SCALE = 1;

const INVALID_DROP_ROTATION = 12;
const INVALID_DROP_SCALE = 1.18;
const VALID_DROP_ROTATION = 6;
const VALID_DROP_SCALE = 1.08;

const SNAP_BACK_MS = 300;

interface DragVisuals {
  rotation: number;
  scale: number;
  shadowClass: string;
}

interface PlacedRewardInfo {
  uid: string;
  stopUid?: string;
  week: number;
  x: number;
  y: number;
  rotation: number;
  zIndex: number;
}

interface BookRewardEntry {
  key: string;
  reward: LpRidScrapbookReward;
  placed?: PlacedRewardInfo;
}

@Component({
  selector: 'rid-scrapbook-activity',
  imports: [
    RidAssignmentBackground,
    NineSlicedContainerComponent,
    RidButton,
    RidDraggable,
    RidDrawingTray,
    RidScrapbookDrawing,
    RidScrapbookDrawingInput,
  ],
  templateUrl: './rid-scrapbook-activity.html',
})
export class RidScrapbookActivity implements ILpActivity<LpRidScrapbookActivity> {
  protected readonly scrapbook = inject(RidScrapbookService);
  protected readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly rewardService = inject(RidRewardService);

  activity = input.required<LpRidScrapbookActivity>();

  protected readonly restRotation = REST_ROTATION;
  protected readonly floatingRestLeft = FLOATING_REST_LEFT;
  protected readonly floatingRestTop = FLOATING_REST_TOP;
  protected readonly contentEl = viewChild<ElementRef<HTMLElement>>('contentEl');
  protected readonly drawingTool = signal<RidDrawingTool | null>(null);

  private readonly placedRewards = computed(() => this.rewardService.placedRewardsIn(this.scrapbook.items()));

  // the uid of the scrapbook item the current reward became once placed
  private readonly currentPlacement = signal<string | undefined>(undefined);

  protected readonly currentReward = computed(() => {
    const reward = this.activity().reward ?? this.rewardService.currentReward();
    if (!reward) return undefined;

    const uid = this.currentPlacement();
    const match = uid ? this.placedRewards().find((r) => r.uid === uid) : undefined;
    return { reward, placed: match && this.toPlacedInfo(match) };
  });

  protected readonly hasPlacedCurrentReward = computed(() => !this.currentReward() || !!this.currentPlacement());

  protected readonly bookRewards = computed<BookRewardEntry[]>(() => {
    const current = this.currentReward();
    const week = this.scrapbook.currentWeekIndex();
    const others = this.placedRewards()
      .filter((r) => r.week === week && r.uid !== this.currentPlacement())
      .map((r) => ({ key: r.uid, reward: r.reward, placed: this.toPlacedInfo(r) }));

    return current && (!current.placed || current.placed.week === week)
      ? [{ key: 'current', reward: current.reward, placed: current.placed }, ...others]
      : others;
  });

  protected readonly draggingKey = signal<string | null>(null);
  protected readonly canDropAtPointer = signal(true);
  private grabRotationFactor = 1;
  private lastPointer: { x: number; y: number } | null = null;
  private dragGeneration = 0;

  protected readonly weekLabel = computed(() => `Week ${this.scrapbook.currentWeekIndex() + 1}`);

  protected readonly bookUrl = computed(() => this.$lpApiService.getAssetUrl(this.activity().book.image));
  protected readonly bookSize = computed<Vector2>(() => {
    const { x, y } = this.activity().book.size;
    return [x, y] as Vector2;
  });

  protected readonly bookContentBox = computed<Rect>(() =>
    this.toPixelRect(this.activity().book.contentBoxMin, this.activity().book.contentBoxMax),
  );
  protected readonly bookPaddingBox = computed<Rect>(() =>
    this.toPixelRect(this.activity().book.paddingBoxMin, this.activity().book.paddingBoxMax),
  );
  protected readonly bookScale = computed(() => this.activity().book.scale);

  close() {
    this.$lpApiService.finishCurrentActivity();
  }

  protected isDraggingEntry(entry: BookRewardEntry): boolean {
    return this.draggingKey() === entry.key;
  }

  protected zIndexFor(entry: BookRewardEntry): number {
    return entry.placed?.zIndex ?? this.scrapbook.nextZIndex();
  }

  protected onRewardDragStarted(entry: BookRewardEntry) {
    this.dragGeneration++;
    if (entry.placed) this.scrapbook.bringToFront(entry.placed.uid);
    this.draggingKey.set(entry.key);
    this.grabRotationFactor = this.rollRotationFactor();
    this.canDropAtPointer.set(!!entry.placed);
  }

  protected onRewardMoved(entry: BookRewardEntry, pointer: { x: number; y: number }) {
    this.lastPointer = pointer;
    this.canDropAtPointer.set(this.fitsWithinContent(pointer, entry.reward));
  }

  protected async onRewardReleased(entry: BookRewardEntry, draggable: RidDraggable) {
    const generation = this.dragGeneration;
    const content = this.contentEl()?.nativeElement;
    const pointer = this.lastPointer;

    if (content && pointer && this.fitsWithinContent(pointer, entry.reward)) {
      const rect = content.getBoundingClientRect();
      const x = pointer.x - rect.left;
      const y = pointer.y - rect.top;
      const rotation = this.placedRotationFromGrab();
      if (entry.placed) {
        const { stopUid } = entry.placed;
        this.scrapbook.updateItem(entry.placed.uid, { type: 'REWARD', uid: entry.reward.uid, stopUid, x, y, rotation });
      } else {
        // a reward set on the activity isn't rolled for a stop, so only a rolled one counts as the stop's reward
        const stopUid = this.activity().reward ? undefined : this.rewardService.currentRoll()?.stopUid;
        this.currentPlacement.set(
          this.scrapbook.addItem(this.scrapbook.currentWeekIndex(), { type: 'REWARD', uid: entry.reward.uid, stopUid, x, y, rotation }),
        );
      }
      requestAnimationFrame(() => {
        if (this.dragGeneration === generation) this.draggingKey.set(null);
      });
      draggable.respawn();
      return;
    }

    await delay(SNAP_BACK_MS);
    if (this.dragGeneration === generation) this.draggingKey.set(null);
  }

  protected visualsFor(isDragging: boolean, idleRotation: number): DragVisuals {
    if (!isDragging) return { rotation: idleRotation, scale: IDLE_SCALE, shadowClass: 'shadow-sm' };

    return this.canDropAtPointer()
      ? { rotation: this.grabRotationFactor * VALID_DROP_ROTATION, scale: VALID_DROP_SCALE, shadowClass: 'shadow-xl shadow-black/20' }
      : { rotation: this.grabRotationFactor * INVALID_DROP_ROTATION, scale: INVALID_DROP_SCALE, shadowClass: 'shadow-2xl shadow-black/40' };
  }

  private toPlacedInfo(r: RidPlacedReward): PlacedRewardInfo {
    return {
      uid: r.uid,
      stopUid: r.placed.stopUid,
      week: r.week,
      x: r.placed.x,
      y: r.placed.y,
      rotation: r.placed.rotation,
      zIndex: r.zIndex,
    };
  }

  private rollRotationFactor(): number {
    const sign = Math.random() < 0.5 ? -1 : 1;
    const magnitude = 0.6 + Math.random() * 0.4;
    return sign * magnitude;
  }

  private placedRotationFromGrab(): number {
    return Math.sign(this.grabRotationFactor) * PLACED_ROTATION;
  }

  private fitsWithinContent(pointer: { x: number; y: number }, reward: LpRidScrapbookReward | undefined): boolean {
    const rect = this.contentEl()?.nativeElement.getBoundingClientRect();
    if (!rect) return false;

    const { width, height } = this.footprintFor(reward);
    return (
      pointer.x - width / 2 >= rect.left &&
      pointer.x + width / 2 <= rect.right &&
      pointer.y - height / 2 >= rect.top &&
      pointer.y + height / 2 <= rect.bottom
    );
  }

  protected rewardHeightFor(reward: LpRidScrapbookReward | undefined): number {
    return REWARD_HEIGHT * (reward?.scale ?? 1);
  }

  private footprintFor(reward: LpRidScrapbookReward | undefined): { width: number; height: number } {
    const height = this.rewardHeightFor(reward);
    const w = reward?.image.width;
    const h = reward?.image.height;
    if (w && h) return { width: height * (w / h), height };

    return { width: height, height };
  }

  private toPixelRect(minFraction: { x: number; y: number }, maxFraction: { x: number; y: number }): Rect {
    const { x: width, y: height } = this.activity().book.size;
    return {
      from: [minFraction.x * width, minFraction.y * height] as Vector2,
      to: [maxFraction.x * width, maxFraction.y * height] as Vector2,
    };
  }
}
